import { useCallback, useContext, useMemo } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useRoutedOverlayBase } from "./RoutedOverlayBase";
import { RoutedOverlayChainContext } from "./RoutedOverlayChainContext";
import { useOverlayClose } from "./useOverlayClose";
import {
  createOverlayEntryState,
  decodeRouteSegment,
  encodeRouteSegment,
  getGlobalOverlayBasePath,
  isRouteSafeSegment,
  joinPath,
} from "./routedOverlayUtils";

export interface UseRoutedDrawerOptions {
  /** Static path segment that identifies the drawer, e.g. `"asset"`. */
  segment: string;
  /** Absolute pathname the drawer route is nested under. Defaults to the `RoutedOverlayBase` context. */
  basePath?: string;
  /**
   * `"page"` (default) nests the drawer under `basePath`.
   * `"global"` nests it under whatever page is current, e.g. `/any/page/asset/42`. Inside a
   * `DrawerStackProvider` with `routes`, ids of the registered drawers in the URL are not
   * mistaken for `segment`.
   */
  mode?: "page" | "global";
}

export interface RoutedDrawerControls {
  isOpen: boolean;
  /** Decoded entity id from the URL, or `undefined` when closed. */
  id: string | undefined;
  /** Path below the drawer id (tab and nested overlays), without a leading slash. */
  rest: string;
  /** Pathname the drawer lives at, e.g. `/page/asset/42`. Use as the base for tabs and nested overlays. */
  drawerPath: string | undefined;
  /** Pathname the drawer returns to on close. */
  basePath: string | undefined;
  open: (id: string | number, options?: { tab?: string }) => void;
  close: () => void;
}

/**
 * Drives a detail drawer from the URL (`{base}/{segment}/{id}/*`) so opening it
 * creates a history entry, Back closes it, and refresh or a shared link restores it.
 */
export const useRoutedDrawer = ({
  segment,
  basePath,
  mode = "page",
}: UseRoutedDrawerOptions): RoutedDrawerControls => {
  const location = useLocation();
  const navigate = useNavigate();
  const closeOverlay = useOverlayClose();
  const contextBase = useRoutedOverlayBase();
  const chain = useContext(RoutedOverlayChainContext);

  const resolvedBase = useMemo(() => {
    if (mode === "global") {
      return getGlobalOverlayBasePath(location.pathname, segment, {
        requireId: true,
        chain,
      });
    }
    const base = basePath ?? contextBase;
    return base === undefined ? undefined : joinPath(base);
  }, [mode, segment, basePath, contextBase, location.pathname, chain]);

  const match = useMemo(() => {
    if (resolvedBase === undefined) {
      return null;
    }
    return matchPath(
      { path: `${joinPath(resolvedBase, segment)}/:id/*`, end: false },
      location.pathname,
    );
  }, [resolvedBase, segment, location.pathname]);

  const rawId = match?.params.id;
  const id = rawId ? decodeRouteSegment(rawId) : undefined;
  // matchPath decodes params (including %2F), so take the matched pathname to keep the id encoded.
  const drawerPath = rawId ? match?.pathnameBase : undefined;

  const open = useCallback(
    (nextId: string | number, options?: { tab?: string }) => {
      if (resolvedBase === undefined) {
        return;
      }
      if (!isRouteSafeSegment(nextId)) {
        console.warn(
          `[useRoutedDrawer] Invalid id "${nextId}" for "${segment}"; not opening it.`,
        );
        return;
      }
      navigate(
        {
          pathname: joinPath(
            resolvedBase,
            segment,
            encodeRouteSegment(nextId),
            options?.tab,
          ),
          search: location.search,
        },
        { state: createOverlayEntryState(location) },
      );
    },
    [resolvedBase, segment, navigate, location],
  );

  const close = useCallback(() => {
    if (!match || resolvedBase === undefined) {
      return;
    }
    closeOverlay(resolvedBase);
  }, [match, resolvedBase, closeOverlay]);

  return {
    isOpen: !!match,
    id,
    rest: match?.params["*"] ?? "",
    drawerPath,
    basePath: resolvedBase,
    open,
    close,
  };
};
